import { useState } from "react";
import { PageContainer } from "../../../../components/common/page-container";
import { Heading } from "../../../../components/common/typography";
import { ShimmerLoader, Shimmer } from "../../../../components/common/shimmer/shimmer";
import { SectionCard } from "../../../../components/common/section-card";
import { SegmentedControl } from "../../../../components/common/segmented-control";
import { AdminDateRangePicker, computePresetDates } from "../../../admin-dashboard/presentation/components/admin-date-range-picker";
import { useAdminLoyaltyAnalytics } from "../../hooks/useAdminLoyaltyAnalytics";
import { LoyaltyAnalyticsSummary } from "../components/loyalty-analytics-summary";
import { LoyaltyTrendChart } from "../components/loyalty-trend-chart";

type Granularity = "week" | "month";

const GRANULARITY_OPTIONS: { value: Granularity; label: string }[] = [
  { value: "week", label: "Weekly" },
  { value: "month", label: "Monthly" },
];

const DEFAULT_PRESET = "This Quarter" as const;
const { start: DEFAULT_START, end: DEFAULT_END } = computePresetDates(DEFAULT_PRESET);

export function AdminLoyaltyAnalyticsPage() {
  const [startDate, setStartDate] = useState(DEFAULT_START);
  const [endDate, setEndDate] = useState(DEFAULT_END);
  const [granularity, setGranularity] = useState<Granularity>("week");

  const { data, isLoading } = useAdminLoyaltyAnalytics({ startDate, endDate, granularity });

  return (
    <PageContainer>
      <div className="flex flex-col gap-6">
        <Heading variant="h2" tone="dark">
          Loyalty Analytics
        </Heading>

        <AdminDateRangePicker
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          defaultPreset={DEFAULT_PRESET}
        />

        {isLoading || !data ? (
          <ShimmerLoader message="Analyzing loyalty performance…">
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Shimmer key={i} className="h-24 rounded-2xl" />
                ))}
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Shimmer key={i} className="h-24 rounded-2xl" />
                ))}
              </div>
              <Shimmer className="h-80 rounded-2xl" />
            </div>
          </ShimmerLoader>
        ) : (
          <>
            <LoyaltyAnalyticsSummary summary={data.summary} />

            <SectionCard
              title="Trends"
              action={
                <SegmentedControl
                  options={GRANULARITY_OPTIONS}
                  value={granularity}
                  onChange={setGranularity}
                  aria-label="Chart granularity"
                />
              }
            >
              <LoyaltyTrendChart timeSeries={data.timeSeries} granularity={granularity} />
            </SectionCard>
          </>
        )}
      </div>
    </PageContainer>
  );
}
